import { Auth0ContextInterface } from "@auth0/auth0-react/src/auth0-context";
import AgentDomain from "entities/domain/agents/agent-domain";
import { NotificationCustomConfig } from "entities/domain/agents/agent-notification-config-domain";
import MerchantAgentDomain from "entities/domain/merchant-agents/merchant-agent-domain";
import ReportPerAgentDomain from "entities/domain/report-per-agent";
import { AdminAgentDTO, StylePreferencesDTO } from "entities/dto/AdminAgentDTO";
import { AgentDTO } from "entities/dto/AgentDTO";
import { ReportPerAgentDTO } from "entities/dto/ReportPerAgentDTO";
import { adminAgentTransformFromDtoToDomain } from "entities/transformers/admin-agent-transformer";
import { agentTransformFromDtoToDomain } from "entities/transformers/agent-transformer";
import { reportPerAgentTransformFromDtoToDomain } from "entities/transformers/report-per-agent-transformer";
import { colorList } from "util/methods";
import { mutationRequest, request } from "../util/methods";
import { RequestType } from "./request-type";

export interface UpdateNotificationPreferencesPayload {
  agentId: number;
  disabled_channels?: string[];
  custom_configs?: NotificationCustomConfig[];
}

export interface EditAgentPayload {
  id: number;
  name: string;
  surname: string;
  email: string;
  phone?: string;
  role_id: number;
  active: boolean;
  rotation_enabled?: boolean;
  profile_picture_file?: File | null;
  profile_picture_url: string;
  player_ids: string[] | null;
  style_preferences: StylePreferencesDTO | null;
}

class AgentsService {
  public static async getCurrentAgent({
    getAccessTokenSilently,
  }: Auth0ContextInterface): Promise<AgentDomain> {
    const accessToken = await getAccessTokenSilently();

    const data = await request(RequestType.GET, accessToken, "/agents/me");

    const agent: AdminAgentDTO = data.data;

    return adminAgentTransformFromDtoToDomain(agent, colorList[0]);
  }

  public static async getAgents(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number
  ): Promise<MerchantAgentDomain[]> {
    const accessToken = await getAccessTokenSilently();

    const data = await request<AgentDTO[]>(
      RequestType.GET,
      accessToken,
      `/merchants/${merchantId}/agents`
    );

    const agents: AgentDTO[] = data.data;

    return agents.map((a, i) => agentTransformFromDtoToDomain(a, i));
  }

  public static async getAnalyticsReports(
    { getAccessTokenSilently }: Auth0ContextInterface,
    merchantId: number,
    dateFrom: string,
    dateTo: string
  ): Promise<ReportPerAgentDomain[]> {
    const accessToken = await getAccessTokenSilently();

    const queryParams = new URLSearchParams();

    queryParams.append("date_from", dateFrom);
    queryParams.append("date_to", dateTo);

    const queryString = queryParams.toString();

    const data = (
      await request<ReportPerAgentDTO[]>(
        RequestType.GET,
        accessToken,
        `/merchants/${merchantId}/analytics/agents?${queryString}`
      )
    ).data;

    return data.map(reportPerAgentTransformFromDtoToDomain);
  }

  public static async updateAgent(
    { getAccessTokenSilently }: Auth0ContextInterface,
    payload: EditAgentPayload,
    merchantId: number
  ): Promise<MerchantAgentDomain> {
    const accessToken = await getAccessTokenSilently();

    const formData = new FormData();

    formData.append("name", payload.name);
    formData.append("surname", payload.surname);
    formData.append("email", payload.email);
    // Omitted unless set: the API keeps the stored phone when the field is absent.
    if (payload.phone !== undefined) {
      formData.append("phone", payload.phone);
    }
    formData.append("role_id", payload.role_id.toString());
    formData.append("active", payload.active.toString());
    if (payload.rotation_enabled !== undefined) {
      formData.append("rotation_enabled", payload.rotation_enabled.toString());
    }
    if (payload.style_preferences) {
      formData.append(
        "style_preferences",
        JSON.stringify(payload.style_preferences)
      );
    }
    if (payload.player_ids) {
      formData.append("player_ids", payload.player_ids.join(","));
    }
    if (payload.profile_picture_file)
      formData.append("profile_picture", payload.profile_picture_file);
    if (!payload.profile_picture_file)
      formData.append("profile_picture_url", payload.profile_picture_url);

    const agentResponse = await mutationRequest<AgentDTO>(
      RequestType.PUT,
      accessToken,
      `/merchants/${merchantId}/agents/${encodeURIComponent(payload.id)}`,
      formData,
      "multipart/form-data"
    );

    const agent: AgentDTO = agentResponse.data;

    return agentTransformFromDtoToDomain(agent, 0);
  }

  public static async getAgentById(
    { getAccessTokenSilently }: Auth0ContextInterface,
    agentPayload: {
      agentId: string;
    },
    merchantId: number
  ): Promise<MerchantAgentDomain> {
    const accessToken = await getAccessTokenSilently();
    const { agentId } = agentPayload;

    const data = await request<AgentDTO>(
      RequestType.GET,
      accessToken,
      `/merchants/${merchantId}/agents/${agentId}`
    );

    const agent: AgentDTO = data.data;

    return agentTransformFromDtoToDomain(agent, 0);
  }
  public static async updateNotificationPreferences(
    { getAccessTokenSilently }: Auth0ContextInterface,
    payload: UpdateNotificationPreferencesPayload,
    merchantId: number
  ): Promise<{
    disabledChannels: string[];
    customConfigs: NotificationCustomConfig[];
  }> {
    const accessToken = await getAccessTokenSilently();

    const body: Record<string, unknown> = {};

    if (payload.disabled_channels !== undefined) {
      body.disabled_channels = payload.disabled_channels;
    }
    if (payload.custom_configs !== undefined) {
      body.custom_configs = payload.custom_configs;
    }

    const res = await mutationRequest<{
      disabled_channels: string[];
      custom_configs: NotificationCustomConfig[];
    }>(
      RequestType.PATCH,
      accessToken,
      `/merchants/${merchantId}/agents/${payload.agentId}/notifications/preferences`,
      body
    );

    return {
      disabledChannels: res.data.disabled_channels,
      customConfigs: res.data.custom_configs,
    };
  }
}

export default AgentsService;
